import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import i18n from 'i18next';

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(localizedFormat);

const tt = (key: string, fallback: string, params?: Record<string, unknown>) =>
    i18n.t(`SOCIAL_SCHEDULER.TIME.${key}`, { defaultValue: fallback, ...(params || {}) }) as string;

/**
 * Human-friendly scheduling time for a post.
 * - Past: "Just now" / "{{n}}m ago" / "{{n}} hours ago" / "Yesterday at 4:15 PM"
 * - Near future: "Today at 4:55 PM" / "Tomorrow at 9:00 AM" / "{{rel}} · Friday at 4:55 PM"
 * - Far future: "Apr 24 at 4:55 PM"
 *
 * Always in local timezone, localized via the active i18n + dayjs locale.
 */
export function formatPostTime(iso: string | Date): string {
    const d = dayjs(iso);
    if (!d.isValid()) return '';

    const now = dayjs();
    const time = d.format('LT'); // localized time (e.g. 4:55 PM / 16:55)
    const diffHours = d.diff(now, 'hour', true);
    const diffDays = d.diff(now, 'day', true);

    // Past
    if (diffHours < 0) {
        const absHours = Math.abs(diffHours);
        if (absHours < 1) {
            const mins = Math.abs(Math.round(d.diff(now, 'minute')));
            return mins <= 1 ? tt('JUST_NOW', 'Just now') : tt('MIN_AGO', `${mins}m ago`, { count: mins });
        }
        if (absHours < 24) {
            return d.fromNow(); // dayjs locale handles "3 hours ago"
        }
        if (d.isToday()) return tt('TODAY_AT', `Today at ${time}`, { time });
        if (Math.abs(diffDays) < 2) return tt('YESTERDAY_AT', `Yesterday at ${time}`, { time });
        if (Math.abs(diffDays) < 7) return d.fromNow();
        return d.format('MMM D [·] LT');
    }

    // Future
    if (d.isToday()) return tt('TODAY_AT', `Today at ${time}`, { time });
    if (d.isTomorrow()) return tt('TOMORROW_AT', `Tomorrow at ${time}`, { time });
    if (diffDays < 7) return tt('RELATIVE_DAY_AT', `${d.fromNow(true)} · ${d.format('dddd')} at ${time}`, { relative: d.fromNow(true), day: d.format('dddd'), time });
    return d.format('MMM D [·] LT');
}

/**
 * Short variant for compact card displays (calendar pills, list rows).
 */
export function formatPostTimeShort(iso: string | Date): string {
    const d = dayjs(iso);
    if (!d.isValid()) return '';

    const now = dayjs();
    const time = d.format('LT');
    const diffMin = d.diff(now, 'minute');
    const diffDays = d.diff(now, 'day', true);

    if (diffMin < 0) {
        const abs = Math.abs(diffMin);
        if (abs < 60) return abs <= 1 ? tt('JUST_NOW', 'Just now') : tt('MIN_AGO', `${abs}m ago`, { count: abs });
        if (d.isToday()) return tt('TODAY_SHORT', `Today, ${time}`, { time });
        if (Math.abs(diffDays) < 2) return tt('YESTERDAY_SHORT', `Yesterday, ${time}`, { time });
        if (Math.abs(diffDays) < 7) return tt('DAY_SHORT', `${d.format('ddd')}, ${time}`, { day: d.format('ddd'), time });
        return d.format('MMM D, LT');
    }

    if (diffMin < 60) return diffMin <= 1 ? tt('IN_MIN', `in 1m`, { count: 1 }) : tt('IN_MIN', `in ${diffMin}m`, { count: diffMin });
    if (d.isToday()) return tt('TODAY_SHORT', `Today, ${time}`, { time });
    if (d.isTomorrow()) return tt('TOMORROW_SHORT', `Tomorrow, ${time}`, { time });
    if (diffDays < 7) return tt('DAY_SHORT', `${d.format('ddd')}, ${time}`, { day: d.format('ddd'), time });
    return d.format('MMM D, LT');
}
